import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Clients = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Show ALL clients regardless of status — clients persist always
      setClients(response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClient = async (client) => {
    const displayName = client.business_name || client.name || client.contact_name || `Client #${client.id}`;
    if (!window.confirm(`Approve ${displayName} and move to Submitted Deals for underwriting?`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      const res = await axios.post(`${API_BASE}/clients/${client.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const dealId = res?.data?.deal?.id;
      toast({
        title: 'Approved',
        description: dealId
          ? `${displayName} moved to Submitted Deals (Deal #${dealId}).`
          : `${displayName} moved to Submitted Deals.`
      });
      // Redirect so the user sees the new deal instead of wondering what happened.
      navigate('/submitted-deals');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.error || 'Failed to approve client',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Delete ${client.name}? This will remove all associated data (owners, bank accounts, documents). This action cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/clients/${client.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `${client.name} deleted successfully` });
      loadClients();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete client', variant: 'destructive' });
    }
  };

  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Clients</h1>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search clients"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button variant="outline">Export to Excel</Button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Business Name</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Contact</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Industry</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Revenue</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client, idx) => {
              const contactName = client.contact_name
                || `${client.first_name || ''} ${client.last_name || ''}`.trim();
              const businessDisplay = client.business_name || client.name || contactName || '—';
              const phoneDisplay = client.business_phone || client.phone || client.cell_phone || client.home_phone || '';
              return (
              <tr 
                key={idx} 
                className="border-b border-gray-100 hover:bg-orange-50 cursor-pointer transition"
                onClick={() => navigate(`/clients/${client.id}`)}
                data-testid={`client-row-${client.id}`}
              >
                <td className="px-4 py-3 text-gray-900">{client.id}</td>
                <td className="px-4 py-3 text-gray-900 font-medium" data-testid={`client-name-${client.id}`}>{businessDisplay}</td>
                <td className="px-4 py-3 text-gray-700" data-testid={`client-contact-${client.id}`}>{contactName || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{client.industry || '—'}</td>
                <td className="px-4 py-3 text-gray-900">{client.revenue || '—'}</td>
                <td className="px-4 py-3 text-blue-600">{client.email || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{phoneDisplay || '—'}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                    {client.status || 'Active'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApproveClient(client);
                      }}
                    >
                      Approve & Underwrite
                    </button>
                    <button
                      className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClient(client);
                      }}
                      title="Delete Client"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Clients;